import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useSalon } from "../lib/salon";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Trash, Archive, ArrowCounterClockwise, DotsSixVertical, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

// ── helpers ─────────────────────────────────────────────────────────────────
const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const blankStage = () => ({
  id: genId(),
  name: "Service Stage",
  type: "occupied",
  duration_minutes: 30,
});

const blank = () => ({
  name: "",
  description: "",
  stages_template: [blankStage()],
  price: 0,
  active: true,
  stylist_ids: [],
});

/** If a service came from a legacy flat-duration record (no stages_template),
 *  synthesise a single occupied stage so the editor always has stages to work with. */
const stagesFromService = (s) => {
  if (s.stages_template && s.stages_template.length > 0) return s.stages_template;
  return [{
    id: genId(),
    name: s.name || "Service Stage",
    type: "occupied",
    duration_minutes: s.duration_minutes || 30,
  }];
};

const derivedTotal = (stages) => stages.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0);

const FILTERS = [
  { key: "active",   label: "Active",   match: (s) => s.active },
  { key: "archived", label: "Archived", match: (s) => !s.active },
  { key: "all",      label: "All",      match: () => true },
];

// ── StageTypePill ─────────────────────────────────────────────────────────────
/** Small read-only badge shown on service cards. */
function StageTypePill({ type }) {
  if (type === "occupied") {
    return (
      <span className="inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium bg-[#1A3626] text-white">
        Working
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium bg-[#EFEBE3] text-[#5C635A] border border-[#E0DBD0]">
      Processing
    </span>
  );
}

// ── StageTypeToggle ───────────────────────────────────────────────────────────
/** Inline 2-option segmented selector for stage type. */
function StageTypeToggle({ value, onChange, disabled }) {
  return (
    <div className="inline-flex rounded border border-[#E0DBD0] overflow-hidden text-xs font-medium shrink-0" role="group">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange("occupied")}
        className={`px-2.5 py-1.5 transition-colors ${
          value === "occupied"
            ? "bg-[#1A3626] text-white"
            : "bg-white text-[#5C635A] hover:bg-[#F4F1EA]"
        }`}
        data-testid="stage-type-occupied"
      >
        Working
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange("processing")}
        className={`px-2.5 py-1.5 border-l border-[#E0DBD0] transition-colors ${
          value === "processing"
            ? "bg-[#4A6B53] text-white"
            : "bg-white text-[#5C635A] hover:bg-[#F4F1EA]"
        }`}
        data-testid="stage-type-processing"
      >
        Processing
      </button>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────
export default function Services() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [filterKey, setFilterKey] = useState("active");
  const salon = useSalon();
  const currency = salon?.currency_symbol || "$";

  const fetchAll = async () => {
    const [s, st] = await Promise.all([api.get("/services"), api.get("/stylists")]);
    setList(s.data); setStylists(st.data);
  };
  useEffect(() => { fetchAll(); }, []);

  // ── stage management ───────────────────────────────────────────────────────
  const addStage = () => {
    setForm((f) => ({
      ...f,
      stages_template: [...f.stages_template, blankStage()],
    }));
  };

  const removeStage = (idx) => {
    setForm((f) => ({
      ...f,
      stages_template: f.stages_template.filter((_, i) => i !== idx),
    }));
  };

  const updateStage = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      stages_template: f.stages_template.map((s, i) =>
        i === idx ? { ...s, [field]: value } : s
      ),
    }));
  };

  // ── form validation ────────────────────────────────────────────────────────
  const validateForm = () => {
    if (!form.name.trim()) { toast.error("Service name is required"); return false; }
    if (form.stages_template.length === 0) { toast.error("Add at least one stage to this service"); return false; }
    if (!form.stages_template.some((s) => s.type === "occupied")) {
      toast.error("At least one stage must be a 'Working' stage"); return false;
    }
    for (let i = 0; i < form.stages_template.length; i++) {
      const s = form.stages_template[i];
      if (!s.name.trim()) { toast.error(`Stage ${i + 1} needs a name`); return false; }
      const d = Number(s.duration_minutes);
      if (!d || d < 1) { toast.error(`Stage ${i + 1} must have a positive duration`); return false; }
    }
    return true;
  };

  // ── submit ─────────────────────────────────────────────────────────────────
  const submit = async () => {
    if (!validateForm()) return;
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        stages_template: form.stages_template.map((s) => ({
          id: s.id,
          name: s.name.trim(),
          type: s.type,
          duration_minutes: Number(s.duration_minutes),
        })),
        price: Number(form.price),
        active: form.active,
        stylist_ids: form.stylist_ids,
      };
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success(editing ? "Service updated" : "Service created");
      setOpen(false); setEditing(null); setForm(blank());
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // ── archive / restore / delete ─────────────────────────────────────────────
  const archive = async (s, refCount) => {
    const msg = refCount != null
      ? `This service is referenced by ${refCount} existing booking(s) and cannot be permanently deleted. Archive it instead?`
      : `Archive service "${s.name}"? It will be hidden from new bookings but stays attached to historical ones.`;
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/services/${s.id}`, { active: false });
      toast.success("Service archived");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const restore = async (s) => {
    try {
      await api.put(`/services/${s.id}`, { active: true });
      toast.success("Service restored");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Permanently delete "${s.name}"? (Only works if no booking references it.)`)) return;
    try {
      await api.delete(`/services/${s.id}`);
      toast.success("Service deleted");
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === "object" && detail.code === "service_referenced") {
        await archive(s, detail.booking_count);
        return;
      }
      toast.error(formatApiError(detail) || e.message);
    }
  };

  // ── open modal ─────────────────────────────────────────────────────────────
  const openNew = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      description: s.description || "",
      stages_template: stagesFromService(s),
      price: s.price,
      active: s.active,
      stylist_ids: s.stylist_ids || [],
    });
    setOpen(true);
  };

  const toggleStylist = (id) => {
    setForm((f) => ({
      ...f,
      stylist_ids: f.stylist_ids.includes(id)
        ? f.stylist_ids.filter((x) => x !== id)
        : [...f.stylist_ids, id],
    }));
  };

  const totalDuration = derivedTotal(form.stages_template);
  const occupiedTotal = form.stages_template
    .filter((s) => s.type === "occupied")
    .reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0);
  const hasProcessing = form.stages_template.some((s) => s.type === "processing");

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Catalog</div>
          <h1 className="font-heading text-4xl tracking-tight font-light mt-2">Services</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border bg-[#F4F1EA] p-1" data-testid="services-filter">
            {FILTERS.map((f) => {
              const count = list.filter(f.match).length;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilterKey(f.key)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    filterKey === f.key ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`services-filter-${f.key}`}
                >
                  {f.label} <span className="opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
          {isAdmin && (
            <Button onClick={openNew} className="bg-primary hover:bg-[#2B4F39]" data-testid="services-new-btn">
              <Plus size={16} className="mr-1" /> New service
            </Button>
          )}
        </div>
      </div>

      {/* Service grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
        {list.filter(FILTERS.find((f) => f.key === filterKey).match).map((s) => {
          const stages = stagesFromService(s);
          const isMulti = stages.length > 1;
          return (
            <div
              key={s.id}
              className={`surface-card surface-card-hover p-6 relative ${s.active ? "" : "opacity-75"}`}
              data-testid={`service-card-${s.id}`}
            >
              {!s.active && (
                <span
                  className="absolute top-3 right-3 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[#E6E0D3] text-foreground"
                  data-testid={`service-archived-badge-${s.id}`}
                >
                  Archived
                </span>
              )}
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-2">
                  <div className="font-heading text-lg">{s.name}</div>
                  {/* Duration + stage count */}
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5" data-testid={`service-duration-${s.id}`}>
                    <span>{s.duration_minutes} min</span>
                    {isMulti && (
                      <>
                        <span className="opacity-40">·</span>
                        <span data-testid={`service-stage-count-${s.id}`}>{stages.length} stages</span>
                      </>
                    )}
                  </div>
                  {/* Stage type badges — only if multi-stage */}
                  {isMulti && (
                    <div className="flex flex-wrap gap-1 mt-2" data-testid={`service-stage-badges-${s.id}`}>
                      {stages.map((st, i) => (
                        <StageTypePill key={i} type={st.type} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="font-heading text-xl shrink-0">{currency}{s.price?.toFixed?.(0)}</div>
              </div>
              {s.description && <p className="text-sm text-muted-foreground mt-3">{s.description}</p>}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(s.stylist_ids || []).map((id) => {
                  const st = stylists.find((x) => x.id === id);
                  if (!st) return null;
                  return (
                    <span key={id} className="text-[10px] px-2 py-1 rounded-md bg-secondary text-foreground inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} />
                      {st.name}
                    </span>
                  );
                })}
                {(!s.stylist_ids || s.stylist_ids.length === 0) && (
                  <span className="text-xs text-muted-foreground">All stylists</span>
                )}
              </div>
              {isAdmin && (
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => startEdit(s)} data-testid={`service-edit-${s.id}`}>Edit</Button>
                  {s.active ? (
                    <>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => archive(s)}
                        data-testid={`service-archive-${s.id}`}
                        title="Archive — hide from new bookings; keep historical references."
                      >
                        <Archive size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => remove(s)}
                        data-testid={`service-delete-${s.id}`}
                        className="text-destructive"
                        title="Permanently delete — only allowed if no booking references this service."
                      >
                        <Trash size={14} />
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => restore(s)}
                      data-testid={`service-restore-${s.id}`}
                      className="text-[#1A3626]"
                      title="Restore — bring this service back into the active catalog."
                    >
                      <ArrowCounterClockwise size={14} />
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="service-form-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              {editing ? "Edit service" : "New service"}
            </DialogTitle>
            <DialogDescription>Define how the appointment flows — from stylist arrival to finish.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Name */}
            <div className="space-y-2">
              <Label className="overline">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#F9F8F6]"
                placeholder="e.g. Hair Colour, Men's Cut"
                data-testid="service-name-input"
              />
            </div>

            {/* Appointment Flow (stages) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="overline">Appointment Flow</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Define each step — the stylist is with the client during <strong>Working</strong> time and free during <strong>Processing</strong> time.
                  </p>
                </div>
                {/* Live total */}
                <div
                  className="text-right shrink-0 ml-4"
                  data-testid="service-total-duration"
                >
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
                  <div className="font-heading text-lg text-foreground leading-none">{totalDuration}<span className="text-sm font-normal text-muted-foreground ml-0.5">min</span></div>
                  {hasProcessing && (
                    <div className="text-[10px] text-muted-foreground">{occupiedTotal}m working</div>
                  )}
                </div>
              </div>

              {/* Stage rows */}
              <div className="space-y-2" data-testid="stages-list">
                {form.stages_template.map((stage, idx) => (
                  <div
                    key={stage.id}
                    className="flex items-center gap-2 p-3 rounded-lg bg-[#F9F8F6] border border-[#E0DBD0]"
                    data-testid={`stage-row-${idx}`}
                  >
                    {/* Drag handle placeholder (visual only) */}
                    <DotsSixVertical size={14} className="text-muted-foreground shrink-0 cursor-grab opacity-40" />

                    {/* Stage name */}
                    <Input
                      value={stage.name}
                      onChange={(e) => updateStage(idx, "name", e.target.value)}
                      placeholder="Stage name"
                      className="bg-white text-sm flex-1 min-w-0 h-8"
                      data-testid={`stage-name-${idx}`}
                    />

                    {/* Type toggle */}
                    <StageTypeToggle
                      value={stage.type}
                      onChange={(v) => updateStage(idx, "type", v)}
                    />

                    {/* Duration */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Input
                        type="number"
                        min="1"
                        value={stage.duration_minutes}
                        onChange={(e) => updateStage(idx, "duration_minutes", e.target.value)}
                        className="bg-white w-14 text-sm h-8 text-center"
                        data-testid={`stage-duration-${idx}`}
                      />
                      <span className="text-xs text-muted-foreground">min</span>
                    </div>

                    {/* Remove */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeStage(idx)}
                      disabled={form.stages_template.length === 1}
                      title="Remove stage"
                      data-testid={`stage-remove-${idx}`}
                    >
                      <Trash size={13} />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Add stage button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addStage}
                className="w-full border-dashed text-muted-foreground hover:text-foreground hover:border-solid"
                data-testid="stage-add-btn"
              >
                <Plus size={13} className="mr-1.5" /> Add stage
              </Button>

              {/* Processing info hint — only show when a processing stage is present */}
              {hasProcessing && (
                <div className="flex gap-2 p-3 rounded-lg bg-[#EFEBE3] border border-[#E0DBD0] text-xs text-[#5C635A]">
                  <Info size={14} className="shrink-0 mt-0.5 text-[#4A6B53]" />
                  <span>During <strong>Processing</strong> time, the stylist is temporarily free and may take another client alongside — subject to the salon's scheduling rules.</span>
                </div>
              )}
            </div>

            {/* Price */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="overline">Price ({currency})</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="bg-[#F9F8F6]"
                  data-testid="service-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="overline">Status</Label>
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                    data-testid="service-active-switch"
                  />
                  <span className="text-sm text-muted-foreground">{form.active ? "Active" : "Archived"}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="overline">Description <span className="lowercase tracking-normal text-muted-foreground">(optional)</span></Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#F9F8F6]"
                placeholder="Any notes for staff or clients…"
              />
            </div>

            {/* Performed by */}
            <div className="space-y-2">
              <Label className="overline">Performed by</Label>
              <div className="space-y-2 max-h-36 overflow-auto scrollbar-thin border border-border rounded-md p-3 bg-[#F9F8F6]">
                {stylists.map((st) => (
                  <div key={st.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={form.stylist_ids.includes(st.id)}
                      onCheckedChange={() => toggleStylist(st.id)}
                      id={`st-${st.id}`}
                      data-testid={`service-stylist-${st.id}`}
                    />
                    <label htmlFor={`st-${st.id}`} className="text-sm flex items-center gap-2 cursor-pointer">
                      <span className="w-2 h-2 rounded-full" style={{ background: st.color }} />
                      {st.name}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Leave empty to allow all stylists.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-primary hover:bg-[#2B4F39]" data-testid="service-submit-btn">
              {editing ? "Save changes" : "Create service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
